import Dexie, { type Table } from "dexie";
import type { NotaLocalRow, MetaRow } from "./types";

/**
 * Item normalizado vinculado a um documento (NF-e/NFC-e).
 */
export interface ItemLocalRow {
  id?: number;
  documentoChave: string;
  userId: string;
  perfilId?: string;
  cProd: string;
  xProd: string;
  ncm: string;
  cfop: string;
  cst?: string;
  cest?: string;
  qCom: number;
  uCom?: string;
  vUnCom: number;
  vProd: number;
  vICMS?: number;
  vIPI?: number;
  vPIS?: number;
  vCOFINS?: number;
  vFCP?: number;
  vICMSST?: number;
}

/** Cache persistido de cálculos pesados (KPIs, séries, rankings). */
export interface AnalyticsCacheRow {
  id: string;          // ex: `dashboard:${perfilId}:${signature}`
  userId: string;
  tipo: string;        // 'dashboard' | 'fornecedores' | 'ncm-rank' | ...
  payload: any;
  updatedAt: number;
}

class NFeDatabase extends Dexie {
  notas!: Table<NotaLocalRow, number>;
  itens!: Table<ItemLocalRow, number>;
  analyticsCache!: Table<AnalyticsCacheRow, [string, string]>;
  meta!: Table<MetaRow, string>;
  watchedDirectories!: Table<any, string>;
  vault!: Table<{ userId: string, key: CryptoKey }, string>;

  constructor() {
    // Renomeado para v3 para garantir uma instalação limpa com isolamento multi-usuário
    super("NFeLocalDB_v3");

    this.version(5).stores({
      notas: "++id, [userId+chave], [userId+hash], userId, perfilId, loteId, data, mesAno, emitente, cnpjEmit, valor, ncm",
      itens: "++id, [userId+documentoChave], userId, perfilId, ncm, cfop, cst, cProd",
      analyticsCache: "[userId+id], userId, tipo, updatedAt",
      meta: "&key",
      watchedDirectories: "id, userId, active",
      vault: "&userId" // Chave de criptografia persistente por usuário
    });

    this.on("versionchange", () => {
      console.warn("[db] Nova versão detectada. Fechando conexão...");
      this.close();
    });
  }
}

export const db = new NFeDatabase();

export async function getAllChaves(userId: string): Promise<string[]> {
  const arr: string[] = [];
  try {
    await db.notas.where("userId").equals(userId).each((n) => arr.push(n.chave));
  } catch (err) {
    console.error("[db] Erro ao buscar chaves:", err);
  }
  return arr;
}

export async function getAllHashes(userId: string): Promise<string[]> {
  const arr: string[] = [];
  try {
    await db.notas.where("userId").equals(userId).each((n) => n.hash && arr.push(n.hash));
  } catch (err) {
    console.error("[db] Erro ao buscar hashes:", err);
  }
  return arr;
}

export async function clearLocalData() {
  await db.notas.clear();
  await db.itens.clear();
  await db.analyticsCache.clear();
  await db.meta.clear();
}
